import { useMembers, type MembersControllerListV1200 } from '@kidshome/api-contract';
import type { UseQueryResult } from '@tanstack/react-query';
import { useCan } from '@/entities/session';
import { Perm } from '@/shared/lib/permission-codes';

export type MembersGuardedResult = UseQueryResult<MembersControllerListV1200, Error> & {
  /** True when the caller's role lacks `member:read` — the roster read was never attempted (the query
   * stays disabled), so `data`/`isPending`/`isError` reflect that, not a failed fetch. */
  readonly forbidden: boolean;
};

/**
 * `member:read`-gated wrapper over `useMembers` (`GET /v1/members`) — the standard idiom (ADR-0137) for
 * any roster read whose backing endpoint requires a permission the caller's role might not hold (a
 * custom role can omit "View family"). Disables the query when the permission is missing instead of
 * firing a request that would 403, so callers can render an honest message instead of an empty
 * `data.data` masquerading as "no members yet."
 */
export function useMembersGuarded(
  options?: Parameters<typeof useMembers>[0],
): MembersGuardedResult {
  const { can } = useCan();
  const allowed = can(Perm.MemberRead);
  const query = useMembers({ ...options, enabled: allowed && (options?.enabled ?? true) });
  return { ...query, forbidden: !allowed };
}
